import React, { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";

import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import Avatar from "@mui/material/Avatar";
import { styled } from "@mui/material/styles";

import AttachFileIcon from "@mui/icons-material/AttachFile";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ChatBubbleOutlineOutlinedIcon from "@mui/icons-material/ChatBubbleOutlineOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import FileOutlined from "../../assets/FileOutlined.svg";

import { RootContext } from "../../context/RootContext";
import { LoansContext } from "../../context/LoansContext";
import { useUserDisplayName, seedUserDisplayName } from "../../hooks/useUserDisplayName";
import { getCookie } from "../../utils/Common";
import { SUPPORTED_DOCUMENT_ACCEPT, partitionBySupportedFileType } from "../../utils/fileValidation";
import {
  WaiverServiceRecord,
  WaiverChatLine,
  getWaiverChatLines,
  isWaiverChatUnread,
  sendWaiverChatLine,
  downloadWaiverChatDocument,
  markWaiverChatAsRead,
  getWaiverChatLineTimestamp,
} from "./utils";

// Design tokens for this component only — kept local rather than promoted to
// a shared theme, since no other part of the app currently shares this exact
// palette/spacing scale.
const INK = "#1A1A2E";
const MUTED = "#767689";
const FAINT = "#9CA3AF";
const ACCENT = "#3E5EEC";
// Matches the app's MUI theme primary color (main.tsx's createTheme), the
// same color as the loan header's Submit/Escalate button — used only for
// the Send button so it reads as the loan's one consistent "primary action"
// color, while the rest of this panel keeps its own lighter accent.
const PRIMARY = "#362866";
const HAIRLINE = "#EEF0F5";
const SURFACE = "#F8F9FD";
const OWN_TINT = "#EEF1FF";
const DANGER = "#D64545";

// Mirrors the waiver action buttons' own colors AND look (WaiverActionForm.tsx's
// waiver-reject-button/waiver-approve-button/waiver-approve-conditionally-button)
// so the decision-tag chip reads as the exact same decision — Approve is the
// only contained (filled) button there, Reject/Approve Conditionally are
// outlined, and the chip copies that split too.
const DECISION_TAG_STYLES: Record<
  string,
  { background: string; color: string; border?: string }
> = {
  Approve: { background: "#2D6C45", color: "#fff" },
  Reject: { background: "#fff", color: "#B30C00", border: "1px solid #B30C00" },
  // The waiver-service tags this decision's chat line "Approve-Conditionally"
  // (hyphenated, matching the WAIVER_SERVICE_RESPONSE_TYPE wire value
  // "APPROVED-CONDITIONALLY"), not the button's spaced display label.
  "Approve-Conditionally": {
    background: "#fff",
    color: "#FDA31B",
    border: "1px solid #FDA31B",
  },
};
const getDecisionTagStyle = (tag: string) =>
  DECISION_TAG_STYLES[tag] ?? { background: OWN_TINT, color: ACCENT };

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

// Hover-only affordance for a message row — no permanent border/background,
// so the flat list reads as a conversation rather than a stack of cards.
const MessageRow = styled("div")({
  display: "flex",
  gap: 10,
  padding: "6px 8px",
  borderRadius: 8,
  transition: "background-color 120ms ease",
  "&:hover": { backgroundColor: SURFACE },
});

// The composer's pill: border highlights on focus so it's clear where
// typing is about to go, without a permanently loud accent color.
const ComposerShell = styled("div")({
  display: "flex",
  alignItems: "flex-end",
  gap: 4,
  padding: "6px 6px 6px 8px",
  borderRadius: 20,
  border: "1px solid #DBE2FF",
  backgroundColor: SURFACE,
  transition: "border-color 120ms ease, box-shadow 120ms ease",
  "&:focus-within": {
    borderColor: ACCENT,
    boxShadow: `0 0 0 3px ${ACCENT}1F`,
  },
});

const getInitials = (fullName: string | undefined) => {
  const parts = (fullName ?? "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
};

// Compact chip-style attachment — a filename + download affordance, not a
// bordered card competing with the message text above it.
function ChatLineDocuments({
  documentPaths,
  downloadingPath,
  onDownload,
}: {
  documentPaths: string[];
  downloadingPath: string;
  onDownload: (path: string) => void;
}) {
  if (!documentPaths.length) return null;
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 6 }}>
      {documentPaths.map((path, index) => {
        const fileName = path?.split("/")?.pop() ?? path;
        return (
          <div
            key={`${path}-${index}`}
            data-testid="waiver-chat-document-item"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              maxWidth: 260,
              padding: "3px 4px 3px 10px",
              borderRadius: 14,
              background: "#F1F3FA",
            }}
          >
            <img src={FileOutlined} alt="" style={{ width: 13, height: 13, flexShrink: 0 }} />
            <span
              title={fileName}
              style={{
                fontSize: 12,
                color: INK,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {fileName}
            </span>
            <IconButton
              size="small"
              aria-label="download"
              onClick={() => onDownload(path)}
              disabled={downloadingPath === path}
              data-testid="waiver-chat-download-button"
              sx={{ width: 22, height: 22, padding: 0 }}
            >
              {downloadingPath === path ? (
                <CircularProgress size={12} />
              ) : (
                <FileDownloadOutlinedIcon sx={{ fontSize: 14, color: MUTED }} />
              )}
            </IconButton>
          </div>
        );
      })}
    </div>
  );
}

// A flat message row (avatar left, message body first, sender name/time
// below it in a quiet, unbolded caption) — deliberately not a chat "bubble":
// no per-message border/background, so width and shape never vary line to
// line no matter how short or long the text is. isOwnMessage tints the
// name/avatar only, enough to tell a two-party thread apart without needing
// alternating colors.
function ChatLineItem({
  line,
  isOwnMessage,
  downloadingPath,
  onDownload,
}: {
  line: WaiverChatLine;
  isOwnMessage: boolean;
  downloadingPath: string;
  onDownload: (path: string) => void;
}) {
  const { t } = useTranslation();
  // partyId is the sender's Keycloak/party id, same kind of id as a waiver's
  // own createdBy/responseBy — resolve it to a display name the same way.
  const senderName = useUserDisplayName(line.partyId);
  const timestamp = getWaiverChatLineTimestamp(line);
  // The waiver-service auto-posts the underwriter's decision comment (see
  // pricingException.ts's DECISION_CHAT_TAG) as a chat line tagged with the
  // decision itself — "Approve" / "Reject" / "Approve Conditionally" — so
  // it's visually distinguishable from an ordinary back-and-forth message.
  const decisionTag = line.tags?.[0];

  return (
    <MessageRow data-testid="waiver-chat-line">
      <Avatar
        sx={{
          width: 28,
          height: 28,
          fontSize: 11,
          fontWeight: 600,
          background: isOwnMessage ? ACCENT : "#8A8FA3",
          flexShrink: 0,
        }}
      >
        {getInitials(senderName)}
      </Avatar>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 6 }}>
          <span style={{ fontSize: 14, lineHeight: 1.5, color: "#374151", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
            {line.lineText}
          </span>
          {decisionTag ? (
            <span
              data-testid="waiver-chat-line-tag"
              style={{
                display: "inline-block",
                flexShrink: 0,
                padding: "2px 8px",
                borderRadius: 10,
                fontSize: 11,
                fontWeight: 600,
                ...getDecisionTagStyle(decisionTag),
              }}
            >
              {t("waiverChatDecisionTag", { tag: decisionTag })}
            </span>
          ) : null}
        </div>
        <ChatLineDocuments
          documentPaths={line.documentPaths ?? []}
          downloadingPath={downloadingPath}
          onDownload={onDownload}
        />
        <div
          data-testid="waiver-chat-line-author"
          style={{ display: "flex", alignItems: "baseline", gap: 6, flexWrap: "wrap", marginTop: 4 }}
        >
          <span style={{ fontSize: 12, fontWeight: 400, color: isOwnMessage ? ACCENT : MUTED }}>
            {senderName}
          </span>
          {timestamp ? (
            <span style={{ fontSize: 11, fontWeight: 400, color: FAINT }}>
              {moment(timestamp).format("MMM DD, YYYY, hh:mm A")}
            </span>
          ) : null}
        </div>
      </div>
    </MessageRow>
  );
}

// Renders the per-waiver chat thread and composer, embedded inside
// WaiverSummary's own card for the same waiver. Kept as its own component
// (rather than folded into WaiverSummary) since it owns an entirely separate
// concern — sending/reading chat lines — with its own network calls and
// local state.
export default function WaiverChat({ waiver }: { waiver: WaiverServiceRecord }) {
  const { t } = useTranslation();
  const { dynamicConfig, decodedTokenData } = useContext(RootContext) ?? {};
  const { setLoanDetails } = useContext(LoansContext) ?? {};

  const [message, setMessage] = useState("");
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [fileError, setFileError] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [sendError, setSendError] = useState("");
  const [downloadingPath, setDownloadingPath] = useState("");
  // Expanded by default so this doesn't change what's already visible for
  // anyone with an existing thread — the header remains a click target to
  // collapse it away once seen.
  const [isExpanded, setIsExpanded] = useState(true);

  const scrollRef = useRef<HTMLDivElement>(null);

  // Same "current user's party id" resolution used throughout the app
  // (e.g. useAxios's request interceptor) — a cookie set at login, falling
  // back to dynamicConfig for the rare case it isn't set yet. This is the id
  // format the waiver-service's chat actually assigns to lines the current
  // user sends (it's also what's sent as the "partyid" request header on
  // every call), which is why isOwnMessage below works — but it's a
  // different identity space than the Keycloak userId useUserDisplayName
  // resolves via getUserDetails, so the name lookup for it fails on its own.
  const currentPartyId = getCookie("partyId") || dynamicConfig?.partyId;

  // The app already knows its own display name (from the decoded login
  // token) — seed the cache with it under currentPartyId so every message
  // this user has ever sent shows their real name immediately, with no
  // getUserDetails call for an id that lookup can't resolve anyway. A no-op
  // once already seeded or if a real profile already resolved first.
  seedUserDisplayName(
    currentPartyId,
    decodedTokenData?.name || decodedTokenData?.email || decodedTokenData?.user_id,
  );

  const chatLines = getWaiverChatLines(waiver);
  const isUnread = isWaiverChatUnread(waiver, currentPartyId);
  const canSend = !isSending && (!!message.trim() || pendingFiles.length > 0);

  // Gated on isExpanded too — collapsed means the user hasn't actually seen
  // the thread, so marking it read the moment it merely mounts (as it did
  // before this panel was collapsible) would be wrong. Expanding a
  // still-unread thread fires this the same way mounting used to.
  useEffect(() => {
    if (isUnread && isExpanded && waiver.waiverId) {
      markWaiverChatAsRead(setLoanDetails, waiver, currentPartyId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [waiver.waiverId, isUnread, isExpanded]);

  // Keeps the latest message in view as the thread grows, scoped to this
  // panel's own scroll container so it never jumps the surrounding loan page.
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [chatLines.length]);

  // accept only filters the native file-picker dialog — the same allowlist
  // used for waiver-document uploads applies to chat attachments.
  const addFiles = (fileList: FileList | File[]) => {
    const files = Array.from(fileList);
    if (!files.length) return;
    const { supported, unsupported } = partitionBySupportedFileType(files);
    setFileError(unsupported.length ? t("unsupportedFileType") : "");
    if (supported.length) setPendingFiles((prev) => [...prev, ...supported]);
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    addFiles(event.target.files ?? []);
    event.target.value = "";
  };

  const handleRemoveFile = (index: number) => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSend = async () => {
    if (!canSend) return;
    setIsSending(true);
    setSendError("");
    try {
      await sendWaiverChatLine(setLoanDetails, waiver, message.trim(), pendingFiles, currentPartyId);
      setMessage("");
      setPendingFiles([]);
    } catch (err) {
      setSendError(t("waiverChatSendFailed"));
    } finally {
      setIsSending(false);
    }
  };

  // Enter sends (matching every mainstream chat composer); Shift+Enter still
  // inserts a newline for a multi-line message.
  const handleComposerKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  };

  const downloadDocument = async (path: string) => {
    setDownloadingPath(path);
    try {
      const realmHeaders = { "kc-realm": dynamicConfig?.ViteAppKcRealm };
      const url = await downloadWaiverChatDocument(waiver.waiverId ?? "", path, realmHeaders);
      // Programmatically-clicked <a>, same as WaiverSummary's document
      // download — avoids window.open's popup-blocking/tab-closing issues
      // for presigned GCS URLs.
      if (url) {
        const link = document.createElement("a");
        link.href = url;
        link.target = "_blank";
        link.rel = "noopener noreferrer";
        document.body.appendChild(link);
        link.click();
        link.remove();
      }
    } catch (err) {
      console.error("Something went wrong while downloading a chat document. ERROR:", err);
    } finally {
      setDownloadingPath("");
    }
  };

  const messageCountLabel =
    chatLines.length === 1 ? t("waiverChatOneMessage") : t("waiverChatMessageCount", { count: chatLines.length });

  // Header — click to collapse/expand; a quiet inbox-style unread dot
  // instead of a loud chip.
  const renderHeader = () => (
    <div
      data-testid="waiver-chat-toggle"
      role="button"
      tabIndex={0}
      onClick={() => setIsExpanded((prev) => !prev)}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          setIsExpanded((prev) => !prev);
        }
      }}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginBottom: isExpanded ? 10 : 0,
        cursor: "pointer",
      }}
    >
      <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
        <ChatBubbleOutlineOutlinedIcon sx={{ fontSize: 17, color: ACCENT, position: "relative", top: 2 }} />
        <span style={{ fontFamily: "Poppins", fontSize: 14, fontWeight: 600, color: INK }}>
          {t("waiverChatTitle")}
        </span>
        {chatLines.length > 0 ? (
          <span style={{ fontSize: 12, color: MUTED }}>{messageCountLabel}</span>
        ) : null}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        {isUnread ? (
          <div
            data-testid="waiver-chat-unread-chip"
            style={{ display: "flex", alignItems: "center", gap: 6 }}
          >
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#F5A623" }} />
            <span style={{ fontSize: 12, fontWeight: 600, color: "#B8720A" }}>{t("waiverChatUnread")}</span>
          </div>
        ) : null}
        <ExpandMoreRoundedIcon
          sx={{
            fontSize: 20,
            color: MUTED,
            transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 150ms ease",
          }}
        />
      </div>
    </div>
  );

  // Message list — fixed max-height with its own scroll, so a long
  // thread never pushes the rest of the loan page down.
  const renderMessageList = () => (
    <div
      ref={scrollRef}
      style={{
        maxHeight: 320,
        overflowY: "auto",
        borderTop: `1px solid ${HAIRLINE}`,
        borderBottom: chatLines.length ? `1px solid ${HAIRLINE}` : "none",
        padding: chatLines.length ? "6px 0" : 0,
      }}
    >
      {chatLines.length === 0 ? (
        <div
          data-testid="waiver-chat-empty"
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 4,
            padding: "28px 12px",
          }}
        >
          <ChatBubbleOutlineOutlinedIcon sx={{ fontSize: 26, color: "#D8DCE8" }} />
          <span style={{ fontSize: 13, fontWeight: 500, color: MUTED }}>{t("waiverChatEmpty")}</span>
          <span style={{ fontSize: 12, color: FAINT }}>{t("waiverChatEmptyHint")}</span>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column" }}>
          {chatLines.map((line, index) => (
            <ChatLineItem
              key={line.chatLineOrderId ?? index}
              line={line}
              isOwnMessage={Boolean(currentPartyId) && line.partyId === currentPartyId}
              downloadingPath={downloadingPath}
              onDownload={downloadDocument}
            />
          ))}
        </div>
      )}
    </div>
  );

  // Composer
  const renderComposer = () => (
    <div style={{ marginTop: 12 }}>
      {fileError ? (
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8, color: DANGER, fontSize: 12 }}>
          <ErrorOutlineRoundedIcon sx={{ fontSize: 14 }} />
          {fileError}
        </div>
      ) : null}

      {pendingFiles.length > 0 ? (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
          {pendingFiles.map((file, index) => (
            <div
              key={`${file.name}-${index}`}
              data-testid="waiver-chat-pending-file"
              style={{
                display: "flex",
                alignItems: "center",
                gap: 4,
                maxWidth: 220,
                padding: "4px 4px 4px 10px",
                borderRadius: 14,
                background: OWN_TINT,
              }}
            >
              <span
                title={file.name}
                style={{
                  fontSize: 12,
                  color: INK,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {file.name}
              </span>
              <IconButton
                size="small"
                aria-label="remove"
                onClick={() => handleRemoveFile(index)}
                data-testid="waiver-chat-remove-file-button"
                sx={{ width: 18, height: 18, padding: 0 }}
              >
                <CloseRoundedIcon sx={{ fontSize: 13, color: ACCENT }} />
              </IconButton>
            </div>
          ))}
        </div>
      ) : null}

      <ComposerShell onKeyDown={handleComposerKeyDown}>
        <IconButton
          component="label"
          aria-label={t("waiverChatAttach")}
          data-testid="waiver-chat-attach-button"
          sx={{
            width: 32,
            height: 32,
            color: MUTED,
            "&:hover": { backgroundColor: OWN_TINT, color: ACCENT },
          }}
        >
          <AttachFileIcon sx={{ fontSize: 19 }} />
          <VisuallyHiddenInput
            type="file"
            accept={SUPPORTED_DOCUMENT_ACCEPT}
            onChange={handleFileUpload}
            multiple
          />
        </IconButton>

        <TextField
          fullWidth
          multiline
          minRows={1}
          maxRows={6}
          variant="standard"
          placeholder={t("waiverChatMessagePlaceholder")}
          value={message}
          onChange={(event) => setMessage(event.target.value)}
          data-testid="waiver-chat-message-input"
          InputProps={{ disableUnderline: true }}
          sx={{ "& .MuiInputBase-root": { fontSize: 14, padding: "6px 4px" } }}
        />

        <IconButton
          aria-label={t("waiverChatSend")}
          onClick={handleSend}
          disabled={!canSend}
          data-testid="waiver-chat-send-button"
          sx={{
            width: 34,
            height: 34,
            flexShrink: 0,
            backgroundColor: canSend ? PRIMARY : "#E1E5F5",
            color: canSend ? "#fff" : "#A9AFC7",
            "&:hover": { backgroundColor: canSend ? "#2A1E4F" : "#E1E5F5" },
            "&.Mui-disabled": { color: "#A9AFC7" },
          }}
        >
          {isSending ? <CircularProgress size={16} sx={{ color: "inherit" }} /> : <SendRoundedIcon sx={{ fontSize: 17 }} />}
        </IconButton>
      </ComposerShell>

      {sendError ? (
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 8, color: DANGER, fontSize: 12 }}>
          <ErrorOutlineRoundedIcon sx={{ fontSize: 14 }} />
          {sendError}
        </div>
      ) : null}
    </div>
  );

  return (
    <Box data-testid="waiver-chat" sx={{ padding: "16px 16px 12px" }}>
      {renderHeader()}
      {isExpanded ? (
        <>
          {renderMessageList()}
          {renderComposer()}
        </>
      ) : null}
    </Box>
  );
}
